import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import styles from './SignOutLink.module.css';

interface SignOutLinkProps {
  /** Custom text to display. If not provided, shows "Sign out" */
  text?: string;
  /** Whether to show the user's email in the text (e.g., "Not jflorence0@gmail.com? Sign out") */
  showEmail?: boolean;
  /** Custom CSS class */
  className?: string;
}

const SignOutLink: React.FC<SignOutLinkProps> = ({ 
  text, 
  showEmail = false, 
  className = '' 
}) => {
  const { user, logout } = useAuth();
  const [searchParams] = useSearchParams();

  const handleSignOut = () => {
    // Check if we're in an OAuth flow by looking for OAuth parameters (same logic as Onboard)
    const oauthFlow = searchParams.get('oauth_flow') === 'true';
    const oauthRedirect = searchParams.get('oauth_redirect');
    const clientId = searchParams.get('client_id');
    const redirectUri = searchParams.get('redirect_uri');
    const state = searchParams.get('state');
    
    // If we have OAuth parameters, we're in an OAuth flow
    const isOAuthFlow = !!(oauthFlow || oauthRedirect || clientId || redirectUri || state);
    
    if (isOAuthFlow) {
      // In OAuth flow - clear session and redirect back to login/auth step
      
      // Manually do what logout() does, but don't call logout()
      // Clear localStorage and axios headers (same as logout function)
      localStorage.removeItem('auth_user');
      delete axios.defaults.headers.common['Authorization'];

      // Redirect back to the login/auth step with OAuth parameters preserved
      const currentUrl = new URL(window.location.href);
      const params = new URLSearchParams(currentUrl.search);
      
      // Remove any step parameter to go back to the beginning
      params.delete('step');
      
      // Redirect to the same URL but without the step parameter
      const newUrl = `${currentUrl.pathname}?${params.toString()}`;
      window.location.href = newUrl;
    } else {
      // Normal sign out - use default logout behavior
      logout();
    }
  };

  const getDisplayText = () => {
    if (text) {
      return text;
    }
    
    if (showEmail && user?.email) {
      return `Not ${user.email}? Sign out`;
    }
    
    return 'Sign out';
  };

  return (
    <button 
      className={`${styles.signOutLink} ${className}`}
      onClick={handleSignOut}
      type="button"
    >
      {getDisplayText()}
    </button>
  );
};

export default SignOutLink;
